import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { chipAttrsOf, chipNodeMeta, type ChipNodeMeta, type ChipRefAttrs } from "./chip-extension";

/**
 * Per-chip visual state computed by the surface (not stored in the document).
 * Rendered as `data-chip-*` attributes on the chip element via decorations, so
 * state changes never touch the serialized content.
 */
export interface ChipVisualState {
  /** Highlight the chip as invalid (red tint + warning icon). */
  invalid?: boolean;
}

/** Maps a chip's attrs to its visual state; null/undefined means default. */
export type ChipStateGetter = (attrs: ChipRefAttrs) => ChipVisualState | null | undefined;

/**
 * Decorates chip nodes with `data-chip-*` attributes from a state getter. The
 * getter is read through a thunk on every render pass so callers can swap it
 * (via a ref) without rebuilding the editor; dispatch any transaction — e.g.
 * RichChipEditor's `chipStateKey` effect — to force re-evaluation when the
 * data behind the getter changes. `chipNode` names the chip node to decorate —
 * pass the same meta the surface's chip extension was built with.
 */
export function createChipStateExtension(
  getGetter: () => ChipStateGetter | undefined,
  chipNode: ChipNodeMeta = chipNodeMeta(),
) {
  return Extension.create({
    name: "chipState",
    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey("chipState"),
          props: {
            decorations(state) {
              const getState = getGetter();
              if (!getState) return null;
              const decorations: Decoration[] = [];
              state.doc.descendants((node, pos) => {
                if (node.type.name !== chipNode.name) return;
                const chipState = getState(chipAttrsOf(node.attrs, chipNode.typeAttr));
                if (chipState?.invalid) {
                  decorations.push(
                    Decoration.node(pos, pos + node.nodeSize, { "data-chip-invalid": "true" }),
                  );
                }
              });
              return DecorationSet.create(state.doc, decorations);
            },
          },
        }),
      ];
    },
  });
}
